<template>
  <div class="my-[48px] md:my-32 flex px-10 code-example-bg gap-2 flex-col lg:flex-row">
    <div class="min-w-[320px] py-8 shrink-0">
      <div class="flex flex-col gap-2 items-start">
        <button
          v-for="[index, example] of data.examples.entries()"
          :key="example.title"
          class="px-4 py-2 rounded-full cursor-pointer font-semibold code-example-button"
          :class="
            index === selectedExampleIndex
              ? 'selected-example-button text-white m-[10px] text-base'
              : 'unselected-example-button hover:bg-purple-100 border border-neutral-100 text-neutral-600 text-sm'
          "
          @click="selectedExampleIndex = index"
        >
          {{ example.title }}
        </button>
        <OptionsDropdown
          :data="codeExamplesButtons"
          class="w-full rounded-md code-example-dropdown"
          @selected="onOptionSelection($event)"
        >
        </OptionsDropdown>
        <ViewAllCodeSnippets v-if="data.allExamplesHref" :link="data.allExamplesHref" class="view-all-code-snippets" />
      </div>
    </div>
    <div class="flex-1 mb-10 lg:my-[-70px] overflow-hidden">
      <CodeExampleSection
        :key="selectedExampleIndex"
        class="h-full"
        :example="data.examples[selectedExampleIndex].exampleData"
        :is-product-landing-page="true"
      />
    </div>
    <div class="mb-6 overflow-hidden view-all-button-wrapper">
      <link-wrapper v-if="data.allExamplesHref" :href="data.allExamplesHref" class="w-full">
        <button
          class="d-flex items-center justify-center w-full cursor-pointer font-semibold hover:underline py-2.5 px-4 rounded-md text-sm view-all-button"
        >
          {{ $t('components.codeExamples.viewAllCodeSnippets') }}
          <v-icon class="ml-2" name="arrow-right-line" connotation="cta" :size="-5" />
        </button>
      </link-wrapper>
    </div>
  </div>
</template>

<script setup lang="ts">
import { PropType, ref, computed } from 'vue';
import { CodeExamplesProps } from '@bifrost-dev-docs-mfe/common/types/compositeComponents/CodeExamples';
import { VIcon } from '@vonage/vivid-vue';
import OptionsDropdown from '@/components/composite/components/OptionsDropdown.vue';
import ViewAllCodeSnippets from '@/components/composite/components/ViewAllCodeSnippets.vue';
import LinkWrapper from '@/components/markdown/widgets/LinkWrapper.vue';
import CodeExampleSection from '@/components/CodeExampleSection.vue';

const props = defineProps({
  data: { type: Object as PropType<CodeExamplesProps>, required: true },
});
const codeExamplesButtons = computed(() =>
  props.data.examples.map(item => ({
    title: item.title,
  }))
);

const selectedExampleIndex = ref(0);

const onOptionSelection = (selectedIndex: number) => {
  selectedExampleIndex.value = selectedIndex;
};
</script>

<style scoped lang="scss">
div {
  border-style: solid;
  border-width: 0;
}

button {
  font-family: inherit;
  font-size: 100%;
  font-weight: inherit;
  line-height: inherit;
  color: inherit;
  margin: 0;
  padding: 0;
  font-stretch: inherit;
}

.view-all-button {
  border: 1px solid var(--dp-color-cta-500);
  color: var(--dp-color-cta-500) !important;
}

.mdc-select--outlined {
  border: 1px solid var(--vvd-color-canvas) !important;
  border-radius: 6px !important;
}
.code-example-dropdown {
  background: var(--vvd-color-canvas);
}
.selected-example-button {
  background: var(--dp-color-cta-500);
}
.unselected-example-button {
  background: var(--vvd-color-canvas);
}
.code-example-button {
  font: var(--vvd-typography-base-bold);
}

.code-example-bg {
  background-color: var(--dev-docs-code-example-bg);
}
/* Extra small devices (phones, 600px and down) */
@media only screen and (max-width: 600px) {
  .code-example-button {
    display: none;
  }
  .view-all-code-snippets {
    display: none;
  }
  .code-example-dropdown {
    display: block;
  }
  .view-all-button-wrapper {
    display: flex;
  }
}

/* Small devices (portrait tablets and large phones, 600px and up) */
@media only screen and (min-width: 600px) {
  .code-example-button {
    display: none;
  }
  .view-all-code-snippets {
    display: none;
  }
  .code-example-dropdown {
    display: flex;
  }
  .view-all-button-wrapper {
    display: flex;
  }
}

/* Medium devices (landscape tablets, 768px and up) */
@media only screen and (min-width: 768px) {
  .code-example-button {
    display: none;
  }
  .view-all-code-snippets {
    display: none;
  }
  .code-example-dropdown {
    display: flex;
  }
  .view-all-button-wrapper {
    display: flex;
  }
}

/* Large devices (laptops/desktops, 992px and up) */
@media only screen and (min-width: 992px) {
  .code-example-button {
    display: flex;
  }
  .view-all-code-snippets {
    display: flex;
  }
  .code-example-dropdown {
    display: none;
  }
  .view-all-button-wrapper {
    display: none;
  }
}
</style>
